import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import { SettingsProvider } from "./engine/settingsStore.tsx";
import { initAnalytics } from "./lib/analytics.ts";
import { isChunkLoadError, attemptChunkReloadOnce } from "./lib/chunkReload.ts";
import { reportError } from "./lib/errorReporting.ts";
import "./index.css";

// Stale-deploy recovery: when the site is redeployed, a tab that loaded the
// old index keeps requesting old chunk hashes and the next lazy route import
// fails ("Failed to fetch dynamically imported module"). Recover with a
// single guarded reload instead of dropping the player on the error screen.
// Vite fires `vite:preloadError` for lazy-import failures; some surface as
// unhandled promise rejections instead — handle both.
window.addEventListener("vite:preloadError", (e) => {
  e.preventDefault();
  attemptChunkReloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  const reason = (e as PromiseRejectionEvent).reason;
  if (isChunkLoadError(reason)) {
    e.preventDefault();
    attemptChunkReloadOnce();
    return;
  }
  reportError("unhandled_rejection", reason);
});

// Uncaught errors outside React's render tree (event handlers, timers).
// Render errors are reported by ErrorBoundary. Skipped: opaque
// cross-origin "Script error." (no information) and the benign
// ResizeObserver loop notice browsers raise during layout.
window.addEventListener("error", (e) => {
  const message = e.message ?? "";
  if (message === "Script error." || message.includes("ResizeObserver loop")) return;
  if (isChunkLoadError(e.error)) return;
  reportError("window_error", e.error ?? message);
});

// Bootstrap analytics before React mounts. Reads the opt-out preference
// directly from localStorage (the same key SettingsProvider uses) so the
// initial state is correct on first paint — we don't need React in
// scope to make a privacy decision. The settings UI later wires
// `setOptedOut()` to react to runtime toggles.
function readOptedOutFromStorage(): boolean {
  try {
    const raw = localStorage.getItem("chronicles_settings");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed.analyticsOptOut === true;
  } catch {
    return false;
  }
}

// The PostHog project token is public by design (it can only send events,
// never read them), like the Supabase publishable key. It is written here
// because Lovable's build does not pass repo .env values through; an env
// variable still overrides it. analytics.ts only sends from the live domain.
const POSTHOG_PROJECT_TOKEN = "phc_xksYtV7hmiu3C2cmbFajWemZKhTYJ295x6sZ7t9JgTiZ";

initAnalytics({
  key: import.meta.env.VITE_PUBLIC_POSTHOG_KEY || POSTHOG_PROJECT_TOKEN,
  host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
  optedOut: readOptedOutFromStorage(),
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <SettingsProvider>
      <App />
    </SettingsProvider>
  </HelmetProvider>
);
